import { API_URL, apiRequest } from "@/lib/api/client";
import type {
  Trip,
  RoutePoint,
  VehicleEvent,
  DetailedPosition,
  RouteSummary,
  RouteStop,
  IgnitionPeriod,
  DrivingStoppedSummary,
  CommandEvent,
} from "@/types";

// Integração real com os relatórios do Traccar (GET /api/reports/*).
// Todos exigem sessão autenticada (credentials: include) e devolvem JSON
// porque o client já manda Accept: application/json por padrão.

interface TraccarTrip {
  deviceId: number;
  deviceName?: string;
  startTime: string;
  endTime: string;
  startLat: number;
  startLon: number;
  endLat: number;
  endLon: number;
  startAddress?: string;
  endAddress?: string;
  distance: number; // metros
  averageSpeed: number; // nós
  maxSpeed: number; // nós
  duration: number; // ms
}

interface TraccarRoutePosition {
  deviceId: number;
  latitude: number;
  longitude: number;
  fixTime: string;
  speed: number; // nós
  course: number;
}

interface TraccarEvent {
  id: number;
  deviceId: number;
  type: string;
  eventTime: string;
  positionId?: number;
  geofenceId?: number;
  attributes?: Record<string, unknown>;
}

const KNOTS_TO_KMH = 1.852;

function toTrip(t: TraccarTrip): Trip {
  return {
    id: `${t.deviceId}-${t.startTime}`,
    vehicleId: String(t.deviceId),
    vehicleName: t.deviceName,
    startTime: t.startTime,
    endTime: t.endTime,
    startLat: t.startLat,
    startLng: t.startLon,
    endLat: t.endLat,
    endLng: t.endLon,
    startAddress: t.startAddress,
    endAddress: t.endAddress,
    distanceKm: t.distance / 1000,
    averageSpeed: Math.round(t.averageSpeed * KNOTS_TO_KMH),
    maxSpeed: Math.round(t.maxSpeed * KNOTS_TO_KMH),
    durationMs: t.duration,
  };
}

function toRoutePoint(p: TraccarRoutePosition): RoutePoint {
  return {
    vehicleId: String(p.deviceId),
    lat: p.latitude,
    lng: p.longitude,
    timestamp: p.fixTime,
    speed: Math.round(p.speed * KNOTS_TO_KMH),
    bearing: p.course,
  };
}

function toEvent(e: TraccarEvent): VehicleEvent {
  return {
    id: String(e.id),
    vehicleId: String(e.deviceId),
    type: e.type,
    eventTime: e.eventTime,
    geofenceId: e.geofenceId,
    attributes: e.attributes,
  };
}

// Posição completa de /api/reports/route — usada pelo Centro de Histórico
// (tabela/player), que precisa de bem mais campos do que toRoutePoint()
// preserva (só lat/lng/timestamp/speed/bearing).
interface TraccarDetailedPosition {
  id: number;
  deviceId: number;
  latitude: number;
  longitude: number;
  fixTime: string;
  serverTime?: string;
  speed: number; // nós
  course: number;
  altitude: number;
  accuracy: number;
  address?: string | null;
  geofenceIds?: number[] | null;
  attributes: Record<string, unknown>;
}

function toDetailedPosition(p: TraccarDetailedPosition): DetailedPosition {
  const attrs = p.attributes ?? {};
  return {
    id: p.id,
    vehicleId: String(p.deviceId),
    lat: p.latitude,
    lng: p.longitude,
    timestamp: p.fixTime,
    serverTime: p.serverTime,
    speed: Math.round(p.speed * KNOTS_TO_KMH),
    course: p.course,
    altitude: p.altitude,
    accuracy: p.accuracy,
    satellites: typeof attrs.sat === "number" ? attrs.sat : undefined,
    ignition: typeof attrs.ignition === "boolean" ? attrs.ignition : undefined,
    motion: typeof attrs.motion === "boolean" ? attrs.motion : undefined,
    odometer: typeof attrs.odometer === "number" ? attrs.odometer : undefined,
    totalDistance: typeof attrs.totalDistance === "number" ? attrs.totalDistance : undefined,
    address: p.address,
    geofenceIds: p.geofenceIds,
    attributes: attrs,
  };
}

interface TraccarSummary {
  deviceId: number;
  deviceName: string;
  distance: number; // metros
  averageSpeed: number; // nós
  maxSpeed: number; // nós
  startOdometer: number;
  endOdometer: number;
  startTime: string;
  endTime: string;
  engineHours: number; // ms
}

function toSummary(s: TraccarSummary): RouteSummary {
  return {
    vehicleId: String(s.deviceId),
    vehicleName: s.deviceName,
    distanceKm: s.distance / 1000,
    averageSpeed: Math.round(s.averageSpeed * KNOTS_TO_KMH),
    maxSpeed: Math.round(s.maxSpeed * KNOTS_TO_KMH),
    startOdometer: s.startOdometer,
    endOdometer: s.endOdometer,
    startTime: s.startTime,
    endTime: s.endTime,
    engineHoursMs: s.engineHours,
  };
}

interface TraccarStop {
  deviceId: number;
  deviceName: string;
  latitude: number;
  longitude: number;
  address?: string | null;
  startTime: string;
  endTime: string;
  duration: number; // ms
  startOdometer: number; // metros
  endOdometer: number; // metros
}

function toStop(s: TraccarStop): RouteStop {
  return {
    vehicleId: String(s.deviceId),
    vehicleName: s.deviceName,
    lat: s.latitude,
    lng: s.longitude,
    address: s.address,
    startTime: s.startTime,
    endTime: s.endTime,
    durationMs: s.duration,
    startOdometerKm: s.startOdometer / 1000,
    endOdometerKm: s.endOdometer / 1000,
  };
}

// Tipos de evento consultados quando nenhum filtro específico é pedido —
// o Traccar exige ao menos um "type" em /api/reports/events.
const DEFAULT_EVENT_TYPES = [
  "deviceOnline",
  "deviceOffline",
  "deviceMoving",
  "deviceStopped",
  "deviceOverspeed",
  "geofenceEnter",
  "geofenceExit",
  "alarm",
  "ignitionOn",
  "ignitionOff",
  "maintenance",
];

// Traccar aceita "deviceId" repetido para consultar múltiplos veículos numa
// única chamada de relatório (confirmado no bytecode do ReportResource:
// getSummary/getTrips/etc. recebem List<Long> deviceId) — cada item da
// resposta já vem marcado com o deviceId de origem, então não precisa de
// chamada separada por veículo.
function reportQs(
  deviceId: string | string[],
  from: string,
  to: string,
  extra?: [string, string][],
) {
  const ids = Array.isArray(deviceId) ? deviceId : [deviceId];
  const params = new URLSearchParams({ from, to });
  for (const id of ids) params.append("deviceId", id);
  for (const [k, v] of extra ?? []) params.append(k, v);
  return params.toString();
}

// Tipos de evento usados pelos relatórios "Ligado/Desligado", "Cercas e
// Perímetros" e "Comandos" — consultas enxutas (só os tipos relevantes),
// separadas do DEFAULT_EVENT_TYPES genérico usado por events()/"Eventos".
const IGNITION_EVENT_TYPES = ["ignitionOn", "ignitionOff"];
const GEOFENCE_EVENT_TYPES = ["geofenceEnter", "geofenceExit"];
const COMMAND_EVENT_TYPES = ["commandResult", "queuedCommandSent"];

// Acima disso, "deviceId" repetido (um por veículo) + os `type=` da consulta
// somam uma query string grande o bastante pra estourar o buffer de header
// do nginx e voltar 414 Request-URI Too Large — confirmado em log real pra
// uma consulta de frota inteira com 5005 dispositivos (ver
// gpsdrive_sticky_seed_flood_fix_2026-09-04 na memória: "EventosPanel...
// exceeds nginx's URI/header buffer... every 30s", até então não corrigido).
// 300 ids cobre qualquer frota realista numa chamada só; acima disso,
// fetchEvents() abaixo quebra em lotes.
const EVENTS_DEVICE_CHUNK_SIZE = 300;

async function fetchEvents(
  deviceId: string | string[],
  from: string,
  to: string,
  types: string[],
): Promise<VehicleEvent[]> {
  const ids = Array.isArray(deviceId) ? deviceId : [deviceId];
  const typeParams = types.map((t): [string, string] => ["type", t]);
  if (ids.length <= EVENTS_DEVICE_CHUNK_SIZE) {
    const raw = await apiRequest<TraccarEvent[]>(
      `/api/reports/events?${reportQs(ids, from, to, typeParams)}`,
      { method: "GET", credentials: "include", fallback: [] },
    );
    return raw.map(toEvent);
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += EVENTS_DEVICE_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + EVENTS_DEVICE_CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      apiRequest<TraccarEvent[]>(`/api/reports/events?${reportQs(chunk, from, to, typeParams)}`, {
        method: "GET",
        credentials: "include",
        fallback: [],
      }),
    ),
  );
  return results.flat().map(toEvent);
}

export const reportsService = {
  trips: async (deviceId: string | string[], from: string, to: string): Promise<Trip[]> => {
    const raw = await apiRequest<TraccarTrip[]>(
      `/api/reports/trips?${reportQs(deviceId, from, to)}`,
      {
        method: "GET",
        credentials: "include",
        fallback: [],
      },
    );
    return raw.map(toTrip);
  },
  route: async (deviceId: string | string[], from: string, to: string): Promise<RoutePoint[]> => {
    const raw = await apiRequest<TraccarRoutePosition[]>(
      `/api/reports/route?${reportQs(deviceId, from, to)}`,
      {
        method: "GET",
        credentials: "include",
        fallback: [],
      },
    );
    return raw.map(toRoutePoint);
  },
  events: (deviceId: string | string[], from: string, to: string) =>
    fetchEvents(deviceId, from, to, DEFAULT_EVENT_TYPES),

  // Feed "Eventos" do app do cliente (aba Eventos, PWA) — mesmo conjunto de
  // events() acima SOMADO aos tipos de comando (ver COMMAND_EVENT_TYPES),
  // pra cobrir também confirmação/entrega de comandos (ex.: bloqueio) na
  // mesma lista cronológica. O Traccar não expõe qual comando gerou um
  // commandResult (só a resposta crua do rastreador) — por isso o rótulo
  // desses dois tipos em ALERT_TYPE_META é genérico ("Resultado de
  // comando"/"Comando pendente enviado"), nunca "Bloqueado"/"Desbloqueado"
  // inventado.
  allEvents: (deviceId: string | string[], from: string, to: string) =>
    fetchEvents(deviceId, from, to, [...DEFAULT_EVENT_TYPES, ...COMMAND_EVENT_TYPES]),

  // Relatório "Ligado/Desligado": eventos ignitionOn/ignitionOff reais,
  // filtrados sem o resto da lista genérica.
  ignitionEvents: (deviceId: string | string[], from: string, to: string) =>
    fetchEvents(deviceId, from, to, IGNITION_EVENT_TYPES),

  // Relatório "Cercas e Perímetros": entradas/saídas reais de geofence.
  geofenceEvents: (deviceId: string | string[], from: string, to: string) =>
    fetchEvents(deviceId, from, to, GEOFENCE_EVENT_TYPES),

  // Relatório "Comandos": o Traccar não tem endpoint de "histórico de
  // comandos enviados" dedicado (GET /api/commands só lista comandos
  // salvos/modelo, nunca o que foi de fato disparado; tc_commands_queue é
  // só a fila de entrega pendente, não fica histórico). O que existe de
  // real e consultável por período é o evento commandResult (resposta do
  // rastreador) e queuedCommandSent (comando efetivamente entregue depois
  // de enfileirado) — por isso o relatório usa esses dois tipos de evento
  // em vez de inventar uma tabela de auditoria que o Traccar não expõe.
  commandEvents: async (
    deviceId: string | string[],
    from: string,
    to: string,
  ): Promise<CommandEvent[]> => {
    const events = await fetchEvents(deviceId, from, to, COMMAND_EVENT_TYPES);
    return events.map((e) => ({
      id: e.id,
      vehicleId: e.vehicleId,
      vehicleName: "",
      type: e.type as CommandEvent["type"],
      eventTime: e.eventTime,
      result:
        typeof e.attributes?.result === "string" ? (e.attributes.result as string) : undefined,
    }));
  },

  // Versão completa de route() — usada pelo Centro de Histórico (tabela e
  // player), que precisa de satélites/ignição/odômetro/endereço/etc., não
  // só lat/lng/speed/bearing.
  routePositions: async (
    deviceId: string | string[],
    from: string,
    to: string,
  ): Promise<DetailedPosition[]> => {
    const raw = await apiRequest<TraccarDetailedPosition[]>(
      `/api/reports/route?${reportQs(deviceId, from, to)}`,
      { method: "GET", credentials: "include", fallback: [] },
    );
    return raw.map(toDetailedPosition);
  },

  summary: async (
    deviceId: string | string[],
    from: string,
    to: string,
  ): Promise<RouteSummary[]> => {
    const raw = await apiRequest<TraccarSummary[]>(
      `/api/reports/summary?${reportQs(deviceId, from, to)}`,
      {
        method: "GET",
        credentials: "include",
        fallback: [],
      },
    );
    return raw.map(toSummary);
  },

  stops: async (deviceId: string | string[], from: string, to: string): Promise<RouteStop[]> => {
    const raw = await apiRequest<TraccarStop[]>(
      `/api/reports/stops?${reportQs(deviceId, from, to)}`,
      {
        method: "GET",
        credentials: "include",
        fallback: [],
      },
    );
    return raw.map(toStop);
  },

  // Relatório "Ligado/Desligado": pareia ignitionOn/ignitionOff em ordem
  // cronológica por veículo. Um "on" sem "off" correspondente dentro do
  // período fica com endTime/durationMs undefined (ainda ligado ao fim do
  // intervalo consultado) — não inventamos um horário de término.
  onOffPeriods: async (
    deviceId: string | string[],
    from: string,
    to: string,
  ): Promise<IgnitionPeriod[]> => {
    const events = await fetchEvents(deviceId, from, to, IGNITION_EVENT_TYPES);
    const byVehicle = new Map<string, VehicleEvent[]>();
    for (const e of events) {
      if (!byVehicle.has(e.vehicleId)) byVehicle.set(e.vehicleId, []);
      byVehicle.get(e.vehicleId)!.push(e);
    }
    const result: IgnitionPeriod[] = [];
    for (const [vehicleId, evs] of byVehicle) {
      evs.sort((a, b) => new Date(a.eventTime).getTime() - new Date(b.eventTime).getTime());
      for (let i = 0; i < evs.length; i++) {
        const ev = evs[i];
        const state = ev.type === "ignitionOn" ? "on" : "off";
        const next = evs[i + 1];
        const closes = next && next.type !== ev.type;
        result.push({
          vehicleId,
          vehicleName: "",
          state,
          startTime: ev.eventTime,
          endTime: closes ? next.eventTime : undefined,
          durationMs: closes
            ? new Date(next.eventTime).getTime() - new Date(ev.eventTime).getTime()
            : undefined,
        });
      }
    }
    return result.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  },

  // Relatório "Dirigindo/Parado": combina trips (dirigindo) + stops
  // (parado) + summary (quilometragem, horas de motor) num agregado por
  // veículo. "Ocioso" = horas de motor menos tempo dirigindo (motor ligado
  // parado) — fica null quando o Traccar não reporta horas de motor pro
  // dispositivo (nem todo protocolo manda essa métrica), em vez de mostrar
  // um número inventado.
  drivingStopped: async (
    deviceId: string | string[],
    from: string,
    to: string,
  ): Promise<DrivingStoppedSummary[]> => {
    const [trips, stops, summary] = await Promise.all([
      reportsService.trips(deviceId, from, to),
      reportsService.stops(deviceId, from, to),
      reportsService.summary(deviceId, from, to),
    ]);
    const vehicleIds = new Set<string>([
      ...trips.map((t) => t.vehicleId),
      ...stops.map((s) => s.vehicleId),
      ...summary.map((s) => s.vehicleId),
    ]);
    return [...vehicleIds].map((vehicleId) => {
      const drivingMs = trips
        .filter((t) => t.vehicleId === vehicleId)
        .reduce((sum, t) => sum + t.durationMs, 0);
      const stoppedMs = stops
        .filter((s) => s.vehicleId === vehicleId)
        .reduce((sum, s) => sum + s.durationMs, 0);
      const veh = summary.find((s) => s.vehicleId === vehicleId);
      const idleMs = veh?.engineHoursMs != null ? Math.max(0, veh.engineHoursMs - drivingMs) : null;
      return {
        vehicleId,
        vehicleName: veh?.vehicleName ?? "",
        drivingMs,
        stoppedMs,
        idleMs,
        distanceKm:
          veh?.distanceKm ??
          trips.filter((t) => t.vehicleId === vehicleId).reduce((s, t) => s + t.distanceKm, 0),
      };
    });
  },

  // Baixa o .xlsx que o próprio Traccar gera (mesmo arquivo que a interface
  // web oficial exportaria) — não geramos planilha no cliente para os
  // relatórios que mapeiam 1:1 num endpoint nativo. O formato de export do
  // Traccar é por sufixo de path (/api/reports/<kind>/xlsx), não por
  // negociação de Accept header — confirmado no bytecode do ReportResource
  // (@Path "route/{type:xlsx|mail}", idem trips/stops/summary/events).
  exportExcel: async (
    kind: "route" | "trips" | "stops" | "summary" | "events",
    deviceId: string | string[],
    from: string,
    to: string,
    extra?: [string, string][],
  ): Promise<Blob> => {
    const res = await fetch(
      `${API_URL}/api/reports/${kind}/xlsx?${reportQs(deviceId, from, to, extra)}`,
      {
        method: "GET",
        credentials: "include",
      },
    );
    if (!res.ok) throw new Error(`Erro ${res.status} ao exportar ${kind}`);
    return res.blob();
  },
};
